import { useState, useRef, useEffect } from 'react';

const OPENROUTER_API_URL = 'https://openrouter.ai/api/v1/chat/completions';
const OPENROUTER_API_KEY = process.env.REACT_APP_OPENROUTER_API_KEY;

const SYSTEM_PROMPT = `You are Spring Lotus, a privacy-first mental wellness assistant with persistent conversational memory.

You can reference any earlier messages because the full conversation history is provided to you.
You retain information across sessions to provide a continuous and supportive experience.

Do NOT say you lack memory. Instead, use the provided context to show you remember and care about the user's journey.

Provide empathetic, non-clinical, non-diagnostic, supportive responses.
Encourage reflection, emotional awareness, and small positive actions.
Keep responses warm, supportive, and concise (2-3 sentences).
If the user expresses crisis-level distress, gently encourage seeking professional help.`;

function buildConversation(messages) {
  return [
    { role: 'system', content: SYSTEM_PROMPT },
    ...messages,
  ];
}

async function callOpenRouter(messages) {
  if (!OPENROUTER_API_KEY) {
    throw new Error('Missing OpenRouter API key (REACT_APP_OPENROUTER_API_KEY)');
  }

  const resp = await fetch(OPENROUTER_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${OPENROUTER_API_KEY}`,
      'HTTP-Referer': window.location.origin,
      'X-Title': 'Spring Lotus - Mental Wellness',
    },
    body: JSON.stringify({
      model: 'openai/gpt-4o-mini',
      messages: buildConversation(messages),
      temperature: 0.7,
      max_tokens: 300,
    }),
  });

  if (!resp.ok) {
    const body = await resp.text();
    const message = `OpenRouter API error ${resp.status}: ${body}`;
    throw new Error(message);
  }

  const data = await resp.json();
  return data.choices?.[0]?.message?.content ?? '';
}

async function callOpenRouterCBT(thought) {
  if (!OPENROUTER_API_KEY) {
    throw new Error('Missing OpenRouter API key (REACT_APP_OPENROUTER_API_KEY)');
  }

  const resp = await fetch(OPENROUTER_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${OPENROUTER_API_KEY}`,
      'HTTP-Referer': window.location.origin,
      'X-Title': 'Mental Wellness Assistant',
    },
    body: JSON.stringify({
      model: 'openai/gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: `You are a supportive emotional wellness assistant specializing in CBT-style thought reframing.
Analyze the user's thought and provide:
1. The cognitive distortion (e.g., catastrophizing, all-or-nothing thinking)
2. A balanced, realistic reframe
3. A small 2-minute action step

Format your response as JSON with keys: distortion, balancedReframe, smallAction
Keep language warm and supportive, not clinical.`,
        },
        {
          role: 'user',
          content: `Please analyze this thought and provide a CBT-style reframe: "${thought}"`,
        },
      ],
      temperature: 0.7,
      max_tokens: 400,
    }),
  });

  if (!resp.ok) {
    const body = await resp.text();
    const message = `OpenRouter API error ${resp.status}: ${body}`;
    throw new Error(message);
  }

  const data = await resp.json();
  return data.choices?.[0]?.message?.content ?? '';
}

export default function ReflectiveChatPage() {
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem('reflectiveChatMessages');
    return saved ? JSON.parse(saved) : [];
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCBTModal, setShowCBTModal] = useState(false);
  const [cbtThought, setCBTThought] = useState('');
  const [cbtResult, setCBTResult] = useState(null);
  const [cbtLoading, setCBTLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Persist messages to localStorage
    localStorage.setItem('reflectiveChatMessages', JSON.stringify(messages));
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    // Create user message object
    const userMessage = { role: 'user', content: input };

    // Add to messages state - this maintains full conversation history
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      const reply = await callOpenRouter(updatedMessages);
      const aiMessage = {
        role: 'assistant',
        content: reply,
        isCrisis: false,
      };
      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        role: 'assistant',
        content:
          error.message?.includes('Missing OpenRouter API key')
            ? 'OpenRouter API key is not set. Please add REACT_APP_OPENROUTER_API_KEY to your environment.'
            : 'Connection error. Please try again.',
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCBT = async () => {
    if (!cbtThought.trim()) return;

    setCBTLoading(true);
    try {
      const content = await callOpenRouterCBT(cbtThought);
      // OpenRouter is asked to respond with a JSON object in the response text
      try {
        const parsed = JSON.parse(content);
        setCBTResult({
          distortion: parsed.distortion || 'Thought pattern identified',
          balancedReframe: parsed.balancedReframe || 'A more balanced perspective...',
          smallAction: parsed.smallAction || 'Take a moment to breathe',
        });
      } catch (parseError) {
        // If parsing fails, show the raw response text
        setCBTResult({
          distortion: 'Thought pattern identified',
          balancedReframe: content,
          smallAction: 'Take a moment to reflect',
        });
      }
    } catch (error) {
      console.error('CBT generation error:', error);
      const message = error.message?.includes('Missing OpenRouter API key')
        ? 'OpenRouter API key is not set. Please add REACT_APP_OPENROUTER_API_KEY to your environment.'
        : 'Connection error. Please try again.';
      setCBTResult({
        distortion: 'Error',
        balancedReframe: message,
        smallAction: 'Please try again',
      });
    } finally {
      setCBTLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    localStorage.removeItem('reflectiveChatMessages');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">💬 Reflective Chat</h1>
          <p className="text-gray-600">A supportive space for emotional reflection and awareness</p>
        </div>

        {/* Privacy Notice */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg mb-6">
          <p className="text-sm text-blue-900">
            <strong>Privacy Notice:</strong> Messages are stored locally in your browser to support your wellness journey and are not sent to any backend server. You can clear your history at any time.
          </p>
        </div>

        {/* Chat Container */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200 flex flex-col h-96">
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-center">
                <div>
                  <p className="text-gray-500 text-lg mb-2">Welcome to Reflective Chat</p>
                  <p className="text-gray-400 text-sm">
                    Share your thoughts and feelings. I'm here to listen and support your emotional awareness.
                  </p>
                </div>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs px-4 py-3 rounded-lg ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-br-none'
                        : msg.isCrisis
                        ? 'bg-red-50 text-red-900 border border-red-200 rounded-bl-none'
                        : 'bg-gray-100 text-gray-800 rounded-bl-none'
                    }`}
                  >
                    <p className="text-sm">{msg.content}</p>
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-800 px-4 py-3 rounded-lg rounded-bl-none">
                  <div className="flex gap-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Share your thoughts..."
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                Send
              </button>
            </form>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6 justify-center">
          <button
            onClick={clearChat}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-all font-semibold"
          >
            Clear Chat
          </button>
          <button
            onClick={() => setShowCBTModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all font-semibold"
          >
            Generate Custom CBT Exercise
          </button>
        </div>

        {/* CBT Modal */}
        {showCBTModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">🧠 Custom CBT Exercise</h2>

              {!cbtResult ? (
                <div className="space-y-4">
                  <label className="block font-semibold text-gray-800">
                    Enter a thought you'd like to reframe:
                  </label>
                  <textarea
                    value={cbtThought}
                    onChange={(e) => setCBTThought(e.target.value)}
                    placeholder="Example: I always mess everything up..."
                    className="w-full h-24 p-3 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none resize-none"
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={handleGenerateCBT}
                      disabled={cbtLoading || !cbtThought.trim()}
                      className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 font-semibold"
                    >
                      {cbtLoading ? 'Generating...' : 'Generate'}
                    </button>
                    <button
                      onClick={() => {
                        setShowCBTModal(false);
                        setCBTThought('');
                        setCBTResult(null);
                      }}
                      className="flex-1 bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300 transition-all font-semibold"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                    <p className="text-xs font-semibold text-red-700 mb-1">Thought Pattern:</p>
                    <p className="text-gray-800">{cbtResult.distortion}</p>
                  </div>

                  <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
                    <p className="text-xs font-semibold text-green-700 mb-1">Balanced Reframe:</p>
                    <p className="text-gray-800">{cbtResult.balancedReframe}</p>
                  </div>

                  <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded">
                    <p className="text-xs font-semibold text-indigo-700 mb-1">2-Minute Action:</p>
                    <p className="text-gray-800">{cbtResult.smallAction}</p>
                  </div>

                  <button
                    onClick={() => {
                      setShowCBTModal(false);
                      setCBTThought('');
                      setCBTResult(null);
                    }}
                    className="w-full bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300 transition-all font-semibold"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-12 bg-white rounded-xl shadow-md p-6 border border-gray-200">
          <h3 className="text-xl font-bold text-gray-800 mb-4">💡 About This Chat</h3>
          <div className="space-y-3 text-gray-700 text-sm">
            <p>
              <strong>What it is:</strong> A supportive space for emotional reflection and awareness. This AI is designed to listen, validate, and encourage self-reflection.
            </p>
            <p>
              <strong>What it's NOT:</strong> This is not therapy, diagnosis, or medical advice. If you're in crisis, please reach out to a professional.
            </p>
            <p>
              <strong>Privacy:</strong> Your messages are stored locally in your browser for continuity and reflection. They are never stored on a server.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
